import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AlertCircle, File, X } from "lucide-react";
import { Spinner } from "../../Spinner/Spinner";
import {
  WorkspaceCardFooterTitleRow,
  WorkspaceCardFooterTypeIcon,
} from "../WorkspaceCardFooterTypeIcon";
import { WorkspaceGridCard } from "../WorkspaceGridCard";
import {
  gridCardFooterMetaRowClassName,
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
} from "../constants";
import type { PendingFileEntry } from "../types";

export interface UploadingCardProps {
  entry: PendingFileEntry;
  onDismiss?: (id: string) => void;
}

export function UploadingCard({ entry, onDismiss }: UploadingCardProps) {
  const isError = entry.status === "error";
  const showDismiss = entry.dismissable && !!onDismiss;
  const dismissLabel = isError ? "Dismiss" : "Cancel upload";

  const preview = (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-card">
      {isError ? (
        <AlertCircle className="h-8 w-8 text-destructive/40" />
      ) : (
        <Spinner size="lg" className="text-foreground/20" />
      )}
    </div>
  );

  const footer = (
    <>
      <div className="min-w-0 flex-1">
        <WorkspaceCardFooterTitleRow
          icon={<WorkspaceCardFooterTypeIcon icon={File} />}
        >
          <p className={gridCardFooterTitleClassName}>{entry.name}</p>
        </WorkspaceCardFooterTitleRow>
        <p
          className={cn(
            gridCardFooterMetaRowClassName,
            gridCardFooterSubtitleClassName,
            isError && "text-destructive",
          )}
        >
          {isError ? "Upload failed" : "Uploading..."}
        </p>
      </div>
      {showDismiss && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={dismissLabel}
          title={dismissLabel}
          className="h-6 w-6 shrink-0 rounded-md text-foreground hover:text-foreground"
          onClick={() => onDismiss?.(entry.id)}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </>
  );

  return (
    <WorkspaceGridCard
      preview={preview}
      footer={footer}
      dashed
      previewClassName={
        isError
          ? "border-destructive/50 group-hover:border-destructive/50"
          : undefined
      }
    />
  );
}
